import type { ReactNode } from 'react';

import { Box } from './Box.js';
import type { BoxProps } from './Box.js';
import { cx } from './cx.js';

type Gap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around';

const gapClass: Record<Gap, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
};

const alignClass: Record<Align, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const justifyClass: Record<Justify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

export type StackProps = BoxProps & {
  gap?: Gap;
  align?: Align;
  justify?: Justify;
  wrap?: boolean;
  children?: ReactNode;
};

function makeStack(direction: string) {
  return function StackImpl({
    gap = 0,
    align,
    justify,
    wrap = false,
    className,
    children,
    ...rest
  }: StackProps) {
    return (
      <Box
        className={cx(
          'flex',
          direction,
          gapClass[gap],
          align && alignClass[align],
          justify && justifyClass[justify],
          wrap && 'flex-wrap',
          className,
        )}
        {...rest}
      >
        {children}
      </Box>
    );
  };
}

export const VStack = makeStack('flex-col');
export const HStack = makeStack('flex-row');
